/**
 * Best-match ordering for the CRM search page's entity groups (search-quality spec, Part 2 amendment).
 *
 * A name-match tier, lowest = best: the exact → prefix → text-match ladder the note @-mention ranker
 * uses server-side (`notes.py`), plus a word-prefix rung so "Ash Hunt" ranks "Ashley Hunt" above a
 * name that merely contains the letters. It runs client-side because the groups come from two engines:
 * Meili's relevance covers people and orgs, but the deal lookup is SQL and returns record-id order with
 * no score at all, so the only comparable signal across the three is how well each NAME matches.
 */

/** Tier for a string that does not contain every query word (e.g. Meili matched an alias). */
export const NO_MATCH = 4;

const normalize = (s: string) => s.toLowerCase().split(/\s+/).filter(Boolean).join(' ');
const words = (s: string) => s.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);

/** 0 exact · 1 starts with the query · 2 every query word starts a word · 3 every word appears · 4 none. */
export function matchTier(text: string | null | undefined, query: string): number {
  const t = normalize(text ?? '');
  const q = normalize(query);
  if (!t || !q) return NO_MATCH;
  if (t === q) return 0;
  if (t.startsWith(q)) return 1;
  const qWords = words(q);
  if (qWords.length === 0) return NO_MATCH;
  const tWords = words(t);
  if (qWords.every((w) => tWords.some((tw) => tw.startsWith(w)))) return 2;
  if (qWords.every((w) => t.includes(w))) return 3;
  return NO_MATCH;
}

/** A row's best tier across its matchable strings — the name, plus an email or domain so a query that
 *  IS an address ranks the row it names. */
export function rowTier(texts: (string | null | undefined)[], query: string): number {
  return texts.reduce<number>((best, s) => Math.min(best, matchTier(s, query)), NO_MATCH);
}

/** Groups ordered by their best row's tier; ties (and empty groups) keep the order given, which is the
 *  spec's default. `Array.prototype.sort` is stable, so the input order IS the tiebreak. */
export function orderByBestMatch<T extends { tiers: number[] }>(groups: T[]): T[] {
  const best = (g: T) => (g.tiers.length ? Math.min(...g.tiers) : Number.POSITIVE_INFINITY);
  return [...groups].sort((a, b) => best(a) - best(b));
}
